// Quarterly plan: the content home (handoff section 2 — "Content home is
// the Quarterly Plan, not a kanban"). Theme + KPI targets from
// content_quarters; the week grid derives from content_log.plan_week,
// the Social column from the woven social_posts schedule, and the
// newsletter is the recurring Thursday ritual.

import { useEffect, useMemo, useState } from 'react';
import { useContent } from '../../../context/ContentContext';
import { apiFetch } from '../../../utils/apiFetch';
import { quarterOf, nextQuarterOf, mondaysOfQuarter, toDateKey } from '../stages';
import { parseLocalDate } from '../dates';
import { Card, Eyebrow, Pill, Dot } from '../ui';

export default function PlanView() {
  const { entries } = useContent();
  const current = quarterOf();
  const next = nextQuarterOf();
  const [quarter, setQuarter] = useState(current);
  const [plan, setPlan] = useState(null);
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setPlan(null);
    setErr(null);
    apiFetch(`/api/data/content-plan/${quarter}`)
      .then(r => r.json())
      .then(j => { if (!cancelled) setPlan(j.plan || null); })
      .catch(e => { if (!cancelled) setErr(e.message); });
    return () => { cancelled = true; };
  }, [quarter]);

  const mondays = useMemo(() => mondaysOfQuarter(quarter), [quarter]);

  // Woven social posts across the whole quarter, counted per week for the
  // Social column (the context's schedule window is shorter, so fetch the
  // quarter's span here).
  const [socialByWeek, setSocialByWeek] = useState({});
  useEffect(() => {
    if (mondays.length === 0) { setSocialByWeek({}); return; }
    let cancelled = false;
    const from = toDateKey(mondays[0]);
    apiFetch(`/api/data/social/schedule?from=${from}&weeks=${mondays.length + 1}`)
      .then(r => r.json())
      .then(j => {
        if (cancelled || !Array.isArray(j.posts)) return;
        const counts = {};
        for (const p of j.posts) {
          if (!p.scheduledDate) continue;
          const d = parseLocalDate(p.scheduledDate);
          const monday = new Date(d);
          monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
          const key = toDateKey(monday);
          counts[key] = (counts[key] || 0) + 1;
        }
        setSocialByWeek(counts);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [quarter, mondays]);

  // Items in this quarter, bucketed by plan week + channel.
  const byWeek = useMemo(() => {
    const map = {};
    for (const e of entries) {
      if (e.quarter !== quarter || !e.planWeek) continue;
      const key = toDateKey(parseLocalDate(e.planWeek));
      if (!map[key]) map[key] = { blog: [], youtube: [] };
      map[key][e.channel === 'youtube' ? 'youtube' : 'blog'].push(e);
    }
    return map;
  }, [entries, quarter]);

  const unslotted = useMemo(
    () => entries.filter(e => e.quarter === quarter && !e.planWeek && e.status !== 'idea'),
    [entries, quarter]
  );

  // On-pace: blog anchors published or in flight this quarter vs target.
  const anchorTarget = Number(plan?.kpiTargets?.anchors) || null;
  const anchorsMoving = useMemo(
    () => entries.filter(e => e.quarter === quarter && (e.channel || 'blog') === 'blog' && e.status !== 'idea').length,
    [entries, quarter]
  );
  const anchorsPublished = useMemo(
    () => entries.filter(e => e.quarter === quarter && (e.channel || 'blog') === 'blog' && e.status === 'published').length,
    [entries, quarter]
  );
  const openSlots = mondays.filter(m => !(byWeek[toDateKey(m)]?.blog?.length)).length;

  const pacePill = anchorTarget
    ? anchorsMoving >= anchorTarget
      ? { tone: 'good', label: `On pace · ${anchorsMoving} of ${anchorTarget} anchors` }
      : { tone: 'warn', label: `${anchorsMoving} of ${anchorTarget} anchors planned` }
    : { tone: 'neutral', label: `${anchorsMoving} anchors · no target set` };

  return (
    <div className="flex flex-col gap-3.5">
      <Card lash="teal">
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-line flex-wrap gap-3">
          <div>
            <Eyebrow>Quarter theme</Eyebrow>
            <h3 className="text-navy text-[18px] font-black mt-[3px]">
              {quarter}{plan?.theme ? ` · ${plan.theme}` : plan ? '' : ' · loading…'}
              {plan && !plan.theme && <span className="text-ink-3 font-bold text-[14px]"> · no theme yet</span>}
            </h3>
          </div>
          <div className="flex items-center gap-2.5">
            <Pill tone={pacePill.tone}>{pacePill.label}</Pill>
            <div className="flex gap-1 bg-white border border-line rounded-[8px] p-[3px]">
              {[current, next].map(q => (
                <button
                  key={q}
                  onClick={() => setQuarter(q)}
                  className={`text-[12px] font-bold px-3 py-1 rounded-[6px] ${quarter === q ? 'bg-navy text-white' : 'text-ink-2 hover:text-navy'}`}
                >
                  {q}
                </button>
              ))}
            </div>
            <button
              onClick={() => setEditing(true)}
              className="text-bullogic-blue text-[11px] font-black border border-bullogic-blue/40 rounded-[6px] px-3 py-1.5 hover:bg-bullogic-blue/5"
            >
              {plan?.exists ? 'Edit plan' : 'Set up quarter'}
            </button>
          </div>
        </div>

        {/* week grid */}
        <div>
          <div className="grid grid-cols-[110px_1.4fr_1.4fr_0.8fr_0.8fr] gap-3 px-4 py-2.5 border-b border-line-2 bg-[#faf9f6] text-[10.5px] uppercase tracking-[0.05em] text-ink-3 font-black">
            <div>Week</div><div>Blog anchor</div><div>YouTube</div><div>Social</div><div>Newsletter</div>
          </div>
          {mondays.map(m => {
            const key = toDateKey(m);
            const wk = byWeek[key] || { blog: [], youtube: [] };
            const socialCount = socialByWeek[key] || 0;
            return (
              <div key={key} className="grid grid-cols-[110px_1.4fr_1.4fr_0.8fr_0.8fr] gap-3 px-4 py-2.5 border-b border-line-2 last:border-b-0 items-center text-[13px]">
                <div className="font-black text-navy">{m.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                <Slot items={wk.blog} color="#2596BE" />
                <Slot items={wk.youtube} color="#FF6E83" />
                <div>
                  {socialCount > 0
                    ? <span className="text-[11px] font-bold text-ink-2 bg-line-2 px-2 py-px rounded-[5px]">{socialCount} {socialCount === 1 ? 'post' : 'posts'}</span>
                    : <span className="text-ink-3 italic text-[12px]">open</span>}
                </div>
                <div><span className="text-[11px] font-bold text-ink-2 bg-line-2 px-2 py-px rounded-[5px]">Thu</span></div>
              </div>
            );
          })}
        </div>
      </Card>

      {unslotted.length > 0 && (
        <div className="bg-amber/10 border border-amber/40 rounded-[7px] px-3.5 py-2.5 text-[12.5px] text-ink">
          <b className="text-navy">{unslotted.length} {unslotted.length === 1 ? 'piece' : 'pieces'} in {quarter} without a week:</b>{' '}
          {unslotted.map(e => e.title).join(' · ')}. Assign weeks from the Idea bank promote flow or the Content Log.
        </div>
      )}

      <div className="bg-bullogic-blue/5 border border-dashed border-bullogic-blue/40 rounded-[7px] px-3.5 py-2.5 text-[12.5px] text-ink-2">
        {openSlots > 0
          ? <><b className="text-navy">{openSlots} open blog {openSlots === 1 ? 'week' : 'weeks'} in {quarter}.</b> Promote ideas into slots before the quarter starts; everything planned here flows into This Week.</>
          : <><b className="text-navy">Every week has an anchor.</b> {anchorsPublished} published so far this quarter.</>}
      </div>

      {err && <div className="text-[12px] text-rose">{err}</div>}

      {editing && plan && (
        <PlanEditModal
          plan={plan}
          quarter={quarter}
          onClose={() => setEditing(false)}
          onSaved={p => { setPlan(p); setEditing(false); }}
        />
      )}
    </div>
  );
}

function Slot({ items, color }) {
  if (!items.length) return <div className="text-ink-3 italic text-[12px]">open</div>;
  return (
    <div className="flex flex-col gap-1">
      {items.map(e => (
        <div key={e.id} className="flex items-center gap-2">
          <Dot color={color} />
          <span className={e.status === 'published' ? 'text-ink-3 line-through' : 'text-ink'}>{e.title}</span>
        </div>
      ))}
    </div>
  );
}

function PlanEditModal({ plan, quarter, onClose, onSaved }) {
  const [theme, setTheme] = useState(plan.theme || '');
  const [notes, setNotes] = useState(plan.notes || '');
  const [targets, setTargets] = useState({
    anchors: plan.kpiTargets?.anchors ?? '',
    sessions: plan.kpiTargets?.sessions ?? '',
    subscribers: plan.kpiTargets?.subscribers ?? '',
    booked_calls: plan.kpiTargets?.booked_calls ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const kpiTargets = {};
      for (const [k, v] of Object.entries(targets)) {
        if (v !== '' && Number.isFinite(Number(v))) kpiTargets[k] = Number(v);
      }
      const res = await apiFetch(`/api/data/content-plan/${quarter}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ theme: theme.trim() || null, notes: notes.trim() || null, kpiTargets }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      onSaved(data.plan);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  const input = 'w-full border border-line rounded-[6px] px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:border-bullogic-blue';
  const TARGETS = [
    ['anchors', 'Blog anchors'],
    ['sessions', 'Sessions'],
    ['subscribers', 'New subscribers'],
    ['booked_calls', 'Booked calls'],
  ];

  return (
    <div className="fixed inset-0 z-50 bg-navy/20 grid place-items-center" onClick={onClose}>
      <div className="bg-white border border-line rounded-[10px] shadow-[0_8px_24px_rgba(2,10,40,0.10)] p-4 w-[440px]" onClick={e => e.stopPropagation()}>
        <div className="font-black text-navy text-[14px] mb-3">Plan {quarter}</div>
        <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1">Theme</label>
        <input value={theme} onChange={e => setTheme(e.target.value)} placeholder='e.g. "Entity Architecture & Exit"' className={input} />
        <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1 mt-3">KPI targets</label>
        <div className="grid grid-cols-2 gap-2">
          {TARGETS.map(([k, label]) => (
            <div key={k}>
              <div className="text-[11px] text-ink-2 mb-0.5">{label}</div>
              <input
                type="number"
                value={targets[k]}
                onChange={e => setTargets(t => ({ ...t, [k]: e.target.value }))}
                className={input}
              />
            </div>
          ))}
        </div>
        <label className="block text-[10px] font-black uppercase tracking-wider text-navy mb-1 mt-3">Notes</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className={input} />
        {err && <div className="text-[11px] text-rose mt-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-3.5">
          <button onClick={onClose} className="text-[12px] font-bold text-ink-3 px-3 py-1.5">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="bg-deep-teal text-white text-[12px] font-black rounded-[6px] px-3.5 py-1.5 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save plan'}
          </button>
        </div>
      </div>
    </div>
  );
}
